import { IconPlus } from '@tabler/icons-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getOpportunities, type OpportunityListItem } from '@/api/opportunities';
import type { DataTableResponse } from '@/components/data-table';
import { NewCompanyModal } from '@/components/modals/new-company-modal';
import type { ProspectCompany } from '@/components/prospect-module/prospect-companies-table';
import { ProspectCompaniesTable } from '@/components/prospect-module/prospect-companies-table';
import { Button } from '@/components/ui/button';
import { useOpportunitiesCount } from '@/contexts/opportunities-context';
import { createSortMapper, PROSPECT_COMPANIES_SORT_MAP } from '@/lib/sort-mapping';

interface ProspectCompanyFilters {
  page: number;
  limit: number;
  sortBy: string;
  order: 'asc' | 'desc';
  status?: string[] | undefined;
  priority?: string[] | undefined;
}

const sortMapper = createSortMapper(PROSPECT_COMPANIES_SORT_MAP);

export const ProspectCompanies: React.FC = () => {
  const { setCount: setOpportunitiesCount } = useOpportunitiesCount();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewCompanyModalOpen, setIsNewCompanyModalOpen] = useState(false);
  const [opportunities, setOpportunities] = useState<ProspectCompany[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<ProspectCompanyFilters>({
    page: 1,
    limit: 10,
    sortBy: 'created_at',
    order: 'desc',
  });

  const mapToProspectCompany = useCallback(
    (item: OpportunityListItem): ProspectCompany => ({
      id: item.id,
      name: item.name,
      status: item.status as ProspectCompany['status'],
      priority: item.priority as ProspectCompany['priority'],
      createdAt: item.createdAt,
      lastContact: item.lastContact,
      salesStage: (item.salesStage || 'initial_contact') as ProspectCompany['salesStage'],
      contactCount: item.contactCount || 0,
    }),
    []
  );
  const fetchProspectCompanies = useCallback(
    async (currentFilters: ProspectCompanyFilters) => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await getOpportunities({
          page: currentFilters.page,
          limit: currentFilters.limit,
          status: currentFilters.status,
          priority: currentFilters.priority,
          sortBy: currentFilters.sortBy,
          sortOrder: currentFilters.order,
        });

        if (response.success) {
          setOpportunities(response.data.map(mapToProspectCompany));
          setTotalCount(response.pagination.total);
          setOpportunitiesCount(response.pagination.total);
        } else {
          setError('Failed to load opportunities');
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to load opportunities');
      } finally {
        setIsLoading(false);
      }
    },
    [mapToProspectCompany, setOpportunitiesCount]
  );

  const handleFilterChange = useCallback((filterKey: string, value: string | string[]) => {
    setFilters((prev) => ({
      ...prev,
      page: 1, // Reset to first page when filtering
      [filterKey]: Array.isArray(value) && value.length === 0 ? undefined : value,
    }));
  }, []);

  const handleSortChange = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    const backendSortBy = sortMapper.toBackend(sortBy);
    if (!backendSortBy) return;
    setFilters((prev) => ({
      ...prev,
      sortBy: backendSortBy,
      order,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  useEffect(() => {
    fetchProspectCompanies(filters);
  }, [filters, fetchProspectCompanies]);

  const getProspectCompanyLink = (company: ProspectCompany) => ({
    to: `/prospect-module/${company.id}`,
  });

  const handleNewCompanySuccess = () => {
    fetchProspectCompanies(filters);
    setIsNewCompanyModalOpen(false);
  };

  const tableData: DataTableResponse<ProspectCompany> = {
    data: opportunities,
    pagination: {
      page: filters.page,
      pageSize: filters.limit,
      total: totalCount,
      totalPages: Math.ceil(totalCount / filters.limit),
    },
  };

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Opportunities</h1>
          <div className="flex items-center gap-4">
            <Button onClick={() => setIsNewCompanyModalOpen(true)} className="gap-2">
              <IconPlus className="h-4 w-4" />
              New Opportunity
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <ProspectCompaniesTable
          data={tableData}
          isLoading={isLoading}
          error={error}
          getRowLink={getProspectCompanyLink}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          currentSortKey={sortMapper.toColumn(filters.sortBy) ?? 'createdAt'}
          currentSortOrder={filters.order ?? 'desc'}
          onPageChange={handlePageChange}
        />
      </div>

      <NewCompanyModal
        open={isNewCompanyModalOpen}
        onClose={() => setIsNewCompanyModalOpen(false)}
        onSuccess={handleNewCompanySuccess}
      />
    </div>
  );
};
